import React, { useEffect, useState } from "react";
import { LayoutGrid, Loader2, ShieldCheck } from "lucide-react";
import api from "../../api";
import { CollapsibleList } from "./CollapsibleList";
import { useToast } from "../../hooks/use-toast";

// Switch a whole category off the shelf. Deals already live in it stay up — new ones can't be posted.
export const AdminCategories = () => {
  const { toast } = useToast();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => { api.get("/admin/categories").then((r) => setRows(r.data.categories)).catch(() => setRows([])); }, []);

  const toggle = async (row) => {
    setBusy(row.name);
    try {
      const { data } = await api.post("/admin/categories", { name: row.name, on: !row.on });
      setRows((rs) => rs.map((r) => (r.name === row.name ? { ...r, on: data.on } : r)));
      toast({ title: data.on ? `${row.name} is live again` : `${row.name} switched off`,
        description: data.on ? "Shops can post in it and shoppers can browse it."
          : `Hidden from the app. ${row.liveDeals} live deal(s) in it stay up until they expire.` });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not change that" }); }
    finally { setBusy(""); }
  };

  if (!rows) return null;

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-categories">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <LayoutGrid size={18} className="text-[#16a34a]" /> Categories
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-4">
        Off means hidden everywhere and closed to new posts. Deals already live in it are never pulled down.
      </p>
      <CollapsibleList collapsedCount={6} className="divide-y divide-border/60" testid="admin-category-list">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-3 py-2.5" data-testid={`admin-category-row-${r.name}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-foreground flex items-center gap-1.5">
                {r.name}
                {r.risky && <ShieldCheck size={13} className="text-[#b8860b]" title="Verified shops only" />}
              </p>
              <p className="text-xs text-muted-foreground">{r.liveDeals} live deal{r.liveDeals === 1 ? "" : "s"}</p>
            </div>
            <button onClick={() => toggle(r)} disabled={busy === r.name} data-testid={`admin-category-toggle-${r.name}`}
              className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-bold rounded-full px-3 py-1.5 border transition-colors ${r.on
                ? "bg-[#e7f6ec] dark:bg-[#16341f] text-[#16a34a] border-[#16a34a]/40"
                : "bg-secondary text-muted-foreground border-border"}`}>
              {busy === r.name ? <Loader2 className="animate-spin" size={13} /> : null}
              {r.on ? "On" : "Off"}
            </button>
          </div>
        ))}
      </CollapsibleList>
    </div>
  );
};

export default AdminCategories;
